<template>
  <div class="tab-group">
    <nav
      class="tab-group__list"
      role="tablist"
      :aria-label="tabGroupTitle"
    >
      <template
        v-for="(tab, index) in tabsWithId"
        :key="index"
      >
        <LinkButton
          :id="`${tab.idPrefix}-button`"
          tag="button"
          type="button"
          role="tab"
          no-styling
          :aria-selected="activeEntry === index ? 'true' : 'false'"
          :aria-controls="`${tab.idPrefix}-panel`"
          class="tab-group__item"
          :class="{ 'tab-group__item--active': activeEntry === index }"
          @click.stop="setActive(index)"
        >
          {{ tab.label }}
        </LinkButton>
      </template>
    </nav>
    <div
      :class="tabGroupContentClass"
      class="tab-group__content"
    >
      <template v-if="!hasLinks">
        <template
          v-for="(tab, index) in tabsWithId"
          :key="index"
        >
          <div
            :id="`${tab.idPrefix}-panel`"
            role="tabpanel"
            :aria-hidden="isActive(index) ? 'false' : 'true'"
            :aria-labelledby="`${tab.idPrefix}-button`"
          >
            <div
              v-show="isActive(index)"
              :class="tabEntryClasses(index, tab)"
              class="tab-group__content"
            >
              <slot
                :label="tab.label"
                :name="tab.name"
                :data="tab.data"
              />
            </div>
          </div>
        </template>
      </template>
      <template v-else>
        <div>
          <slot />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LinkButton } from '@i22team/td-longtail-vue-components';

export type Tab<D = any> = {
  name: string;
  label: string;
  data?: D;
};

export type TabId = Tab & {
  idPrefix: string;
};

export type StageSlideComponentProps = {
  tabs: Tab[];
  active?: number;
  hasLinks?: boolean;
  hasPadding?: boolean;
  tabGroupTitle?: string;
};

const props = withDefaults(defineProps<StageSlideComponentProps>(), {
  active: 0,
  tabGroupTitle: undefined,
});

const uid = ref(useId());

const activeEntry = ref<number>(props.active);

const tabsWithId = computed<TabId[]>(() =>
  props.tabs.map((tab, index) => ({
    ...tab,
    idPrefix: `tab-${uid.value}-${index}`,
  }))
);

watch(
  () => props.active,
  (value) => {
    if (value) {
      activeEntry.value = value;
    }
  }
);

const tabGroupContentClass = computed(() =>
  props.hasPadding ? 'tab-group__content--has-padding' : ''
);

const setActive = (index: number) => {
  if (activeEntry.value === index) return;
  activeEntry.value = index;
};
const isActive = (index: number) => index === activeEntry.value;

const tabEntryClasses = (index: number, tab: Tab) => {
  const selector = `tab-group__content__${tab.name}`;
  const status = isActive(index) ? 'active' : 'inactive';
  return [status, selector];
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.tab-group {
  &__list {
    width: 100%;
    border-bottom: 1px solid var(--color-gray-200);
    text-align: center;
    white-space: nowrap;
    overflow-y: hidden;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-bottom: $grid-base * 2;
  }

  &__item {
    display: inline-block;
    font-weight: 700;
    text-transform: uppercase;
    padding: 5px $grid-base 8px $grid-base;
    border-bottom: 4px solid transparent;
    transition: border 0.125s ease-out;
    border-radius: 0;
    color: currentColor;

    &:hover,
    &--active {
      border-bottom-color: currentColor;
    }

    &--active {
      color: var(--color-primary);
    }

    &:focus-visible {
      outline-offset: -2px;
    }
  }

  &__headline {
    margin-top: 7px;
  }

  &__content {
    margin-bottom: 3 * $grid-base;

    &--has-padding {
      padding-top: 18px;
      @include mq($mq-sm) {
        padding-top: 55px;
      }
    }
  }
}
</style>
